/* =====================================================================================
   Goals, appraisal and evaluation.

   Built to your constraint: people should be doing the job, not spending time looking like
   they are doing the job. Three consequences.

   FIVE OBJECTIVES MAXIMUM, enforced by a trigger in the database. A list of twelve
   priorities is a list of none.

   EVIDENCE IS GATHERED, NOT WRITTEN. Everything the system already knows — interactions
   logged, corrections entered with a rationale, documents authored and approved, MLR reviews
   completed, patent actions closed — is assembled for the period and offered. Nobody spends
   an evening reconstructing their own year.

   WHAT IT CANNOT SEE, IT DOES NOT GUESS. There is no hours count, no login count, no
   activity score. Someone who spent a fortnight on one regulatory submission will show
   almost nothing here, and the panel says so in as many words, because a low count is not a
   low contribution and a system that implies otherwise rewards visible busyness.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import {
  gatherEvidence, fetchObjectives, addObjective, updateObjective, myEmployeeId,
  attachEvidence, OBJECTIVE_STATUS,
  type Objective, type Evidence,
} from '../lib/appraisal';
import { useAutosave } from '../lib/ui';
import { Loading } from './Loading';
import './goals.css';
import { onFail } from '../lib/errors';

function periodDefault() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
  return { from: start, to: now.toISOString().slice(0, 10) };
}

export function GoalsPanel({ employeeId: forced, personName }: {
  employeeId?: string | null; personName?: string;
}) {
  const [employeeId, setEmployeeId] = useState<string | null>(forced ?? null);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [period, setPeriod] = useState(periodDefault());
  const [loading, setLoading] = useState(true);
  const [gathering, setGathering] = useState(false);
  const [adding, setAdding] = useState(false);
  const [n, setN] = useState({ title: '', measure: '', targetDate: '' });
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((x) => x + 1);

  useEffect(() => {
    if (forced) { setEmployeeId(forced); return; }
    myEmployeeId().then(setEmployeeId).catch(onFail('Your employee record', () => setEmployeeId(null)));
  }, [forced]);

  useEffect(() => {
    if (!employeeId) { setLoading(false); return; }
    setLoading(true);
    fetchObjectives(employeeId)
      .then(setObjectives)
      .catch((e) => setErr(String(e.message ?? e)))
      .finally(() => setLoading(false));
  }, [employeeId, nonce]);

  async function gather() {
    if (!employeeId) return;
    setGathering(true); setErr(null);
    try { setEvidence(await gatherEvidence(employeeId, period.from, period.to)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setGathering(false); }
  }

  if (loading) return <Loading label="Loading objectives" />;

  if (!employeeId) {
    return (
      <p className="block-empty">
        Goals attach to an employment record. Yours has not been created yet, which Crew can do.
      </p>
    );
  }

  const atCap = objectives.length >= 5;

  return (
    <div className="goals">
      <header className="goals-head">
        <div>
          <h2>Objectives{personName ? `: ${personName}` : ''}</h2>
          <p className="goals-sub">
            Five at most. A list of twelve priorities is a list of none, so the database
            refuses a sixth.
          </p>
        </div>
        <button className="btn-compose q-new" disabled={atCap} onClick={() => setAdding(!adding)}
                title={atCap ? 'Five is the maximum' : undefined}>
          {adding ? 'Cancel' : atCap ? 'Five of five set' : 'Add objective'}
        </button>
      </header>

      {err && <p className="block-error" role="alert">{err}</p>}

      {adding && (
        <div className="composer card">
          <input placeholder="What are you going to achieve?" value={n.title}
                 onChange={(e) => setN({ ...n, title: e.target.value })} />
          <div className="composer-row">
            {/* How you will know is the part people skip, and it is the part that makes an
                appraisal a conversation about facts rather than impressions. */}
            <input placeholder="How will you know it is done?" className="grow" value={n.measure}
                   onChange={(e) => setN({ ...n, measure: e.target.value })} />
            <label className="composer-ack">By
              <input type="date" value={n.targetDate}
                     onChange={(e) => setN({ ...n, targetDate: e.target.value })} />
            </label>
          </div>
          <div className="composer-actions">
            <button className="btn-save" disabled={!n.title.trim()}
                    onClick={() => addObjective({ employeeId, title: n.title, measure: n.measure,
                      targetDate: n.targetDate || undefined })
                      .then(() => { setAdding(false); setN({ title: '', measure: '', targetDate: '' }); reload(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>
              Add objective
            </button>
          </div>
        </div>
      )}

      {objectives.length === 0 && !adding && (
        <p className="block-empty">
          No objectives set. Start with one or two things that would make the year a success.
        </p>
      )}

      {objectives.map((o) => (
        <ObjectiveCard key={o.id} objective={o} onChanged={reload} />
      ))}

      {/* ---- evidence ---- */}
      <section className="goals-evidence">
        <header className="goals-head">
          <div>
            <h2>Evidence</h2>
            <p className="goals-sub">
              Assembled from work already recorded. Nothing here is a productivity score.
            </p>
          </div>
          <div className="goals-period">
            <input type="date" value={period.from}
                   onChange={(e) => setPeriod({ ...period, from: e.target.value })} />
            <input type="date" value={period.to}
                   onChange={(e) => setPeriod({ ...period, to: e.target.value })} />
            <button className="btn-compose" onClick={gather} disabled={gathering}>
              {gathering ? 'Gathering' : 'Gather evidence'}
            </button>
          </div>
        </header>

        {gathering && <Loading label="Reading what the system already knows" />}

        {!gathering && evidence.length === 0 && (
          <p className="block-empty">
            Nothing gathered yet. Choose a period and press Gather.
          </p>
        )}

        {evidence.length > 0 && (
          <>
            <ul className="ev-list">
              {evidence.map((e, i) => (
                <li key={i} className="ev-item">
                  <span className="pill mute">{e.out_module}</span>
                  <strong>{e.out_headline}</strong>
                  <span className="ev-count">{e.out_count}</span>
                  {e.out_detail && <span className="ev-detail">{e.out_detail}</span>}
                  {objectives.length > 0 && (
                    <select className="ev-attach" defaultValue=""
                            onChange={(ev) => {
                              if (!ev.target.value) return;
                              void attachEvidence(ev.target.value, {
                                module: e.out_module, headline: e.out_headline,
                                detail: e.out_detail ?? undefined,
                                occurredOn: e.out_occurred ?? undefined,
                              });
                              ev.target.value = '';
                            }}>
                      <option value="">Attach to…</option>
                      {objectives.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
                    </select>
                  )}
                </li>
              ))}
            </ul>
            <p className="ev-caveat">
              This is what the system can see. It counts deliberate acts: a conversation you
              chose to log, a correction you stood behind, a review you completed, and
              nothing else. It does not count hours, logins or activity, and a quiet period
              here may be the most valuable work of the year. Say so in the objective itself.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

function ObjectiveCard({ objective, onChanged }: {
  objective: Objective; onChanged: () => void;
}) {
  // Autosave with a visible result, so a long edit is never lost to a stray click.
  const save = useAutosave<Record<string, unknown>>(
    (patch) => updateObjective(objective.id, patch),
  );

  return (
    <article className={`card obj obj-${objective.status}`}>
      <header className="obj-head">
        <input className="obj-title" defaultValue={objective.title}
               onChange={(e) => save.edit({ title: e.target.value })} />
        <div className="obj-right">
          <SaveState state={save.state} error={save.error} />
          <select value={objective.status}
                  onChange={(e) => { save.edit({ status: e.target.value }); void save.flush(); onChanged(); }}>
            {OBJECTIVE_STATUS.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </header>

      <div className="obj-row">
        <label className="obj-field"><span>How will you know</span>
          <input defaultValue={objective.measure ?? ''}
                 onChange={(e) => save.edit({ measure: e.target.value })} />
        </label>
        <label className="obj-field"><span>By</span>
          <input type="date" defaultValue={objective.target_date ?? ''}
                 onChange={(e) => save.edit({ due_on: e.target.value })} />
        </label>
      </div>

      <label className="obj-field"><span>Outcome</span>
        <textarea rows={2} defaultValue={objective.outcome_note ?? ''}
                  placeholder="What actually happened, in your words. The counts below cannot say this."
                  onChange={(e) => save.edit({ outcome_note: e.target.value })} />
      </label>
    </article>
  );
}

/** The confirmation. Autosave without one is worse than no autosave. */
export function SaveState({ state, error }: { state: string; error: string | null }) {
  if (state === 'idle') return null;
  const label = state === 'dirty' ? 'Unsaved'
    : state === 'saving' ? 'Saving'
    : state === 'saved' ? 'Saved'
    : error ?? 'Not saved';
  return <span className={`savestate ${state}`} role="status">{label}</span>;
}
